/*
  Slab configurator — UI logic + Focal theme ATC adapter.

  Reads window.SLAB_CONFIG (emitted by configurator-config.liquid).
  Drives /api/price (live price) and /api/configure (variant + cart line).

  No bundler, no framework. One self-contained IIFE.
*/

'use strict';

(function () {
  if (window.__slabConfiguratorLoaded) return;
  window.__slabConfiguratorLoaded = true;

  const PRICE_DEBOUNCE_MS = 250;
  const FINISH_NOTE_TTL_MS = 4500;

  // ATC flow timings + copy ─ pilot parity. The sync gap gives Shopify time
  // to propagate the just-created variant before /cart/add.js runs.
  const ATC_SYNC_GAP_SECONDS    = 5;
  const ATC_REASSURE_INTERVAL_MS = 1500;
  const ATC_CART_ADD_BACKOFFS_MS = [500, 1500, 3000]; // 422 retries (up to 4 attempts)
  const ATC_REASSURE_MSGS = [
    'Almost there…',
    'Syncing with Shopify…',
    'Just a moment more…',
    'Finalizing your order…',
    'Hang tight…',
    'Wrapping up…',
  ];

  // ── boot ────────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState !== 'loading') initAll();

  function initAll() {
    document.querySelectorAll('slab-configurator').forEach(initOne);
    initPaintModal();
  }

  function initOne(root) {
    if (root.__cfgInitialized) return;
    root.__cfgInitialized = true;

    const cfg = window.SLAB_CONFIG;
    if (!cfg) return console.error('[configurator] SLAB_CONFIG missing');

    const form = document.getElementById(root.dataset.formId);
    if (!form) return console.error('[configurator] form not found:', root.dataset.formId);

    // Per-root context — lets multiple <slab-configurator> elements coexist on
    // the same page without sharing URL keys or fighting over the Hardware
    // selection. `urlPrefix` namespaces query-string keys (`nh_t` vs `t`),
    // `lockedHardware` is the hardwareOptions.id that the customer can't
    // change (Hardware row is hidden); blank means unlocked (top section).
    // `closetMode` is set on the Closet Shelf block — it reuses slab no-hw
    // pricing under the hood but (a) hides thicknesses < 1.75", (b) shows
    // only WC3S + Split Assembly build styles, (c) sends subtype=closet so
    // the backend encodes a "Closet shelf / …" variant title.
    // `slabNoHwMode` is set on the dedicated Slab — No Hardware section —
    // Billy's launch ask is to show only "Slab no HW" as the build chip
    // (WC3S + Split Assembly belong to the Niche / Closet section instead).
    const ctx = {
      urlPrefix:      root.dataset.urlPrefix      || '',
      lockedHardware: root.dataset.lockedHardware || '',
      closetMode:     root.dataset.closetMode === 'true',
      slabNoHwMode:   root.dataset.slabNoHwMode === 'true',
    };

    const refs = collectRefs(root);

    populateAll(refs, cfg, ctx);
    applyPerProductOverrides(refs, cfg, ctx);

    // Restore URL-persisted selections onto the freshly-populated controls
    // BEFORE the gating refreshes run, so they reconcile against the restored
    // state rather than the defaults. Finish / exact-thickness / build-style
    // are applied after their dynamic refresh renders them (below).
    const urlState = readUrlState(ctx);
    applyUrlBasicControls(refs, urlState);

    const state = {
      ctx,                  // see initOne; threaded so wireEvents/schedulePrice can call writeUrlState(refs, state.ctx)
      priceTimer: null,
      noteTimer: null,
      lastPriceKey: null,
      lastPrice: null,
      lastPricedWidth: null,
      lastDepthRange: null,
      // Add-on selection state. Per-addon shape mirrors what
      // src/services/addon.service.js#canonicalizeSelection accepts. Empty
      // blocks (qty=0 / blank colorName) are filtered out before sending
      // to /api/price + /api/configure so the network payload stays small.
      addons: {
        led:   { qty: 0, items: [] },
        puck:  { qty: 0, items: [], notes: '', sketchFileName: '' },
        // Custom paint is no longer a per-shelf addon — it's a single flat
        // $85 line item added via the paint modal (see initPaintModal).
      },
    };

    wireEvents(refs, cfg, state);
    wireBuildStyleModal(refs);
    wireAddons(refs, cfg, state);

    // Initial pass — order matters: material decides finish; hardware decides
    // depth ceiling; depth+hardware decide build styles; all of those plus
    // material decide which add-ons are visible.
    applyMaterialFinishOverrideIfNeeded(refs, cfg, /*silent*/ true);
    refreshFinishAvailability(refs, cfg, /*silent*/ true);
    applyUrlFinish(refs, urlState);
    refreshDepthOptionsByHardware(refs, cfg);
    applyDimCeilingRules(refs);
    refreshHardwareByDepth(refs, cfg);
    // Thickness re-renders after URL hardware is restored so a URL like
    // `?hw=slab&t=1.25%22` ends up with 1.25" visible AND selected. The
    // initial populateAll ran with the default hardware (yes) so 1.25"
    // was filtered out at that point.
    refreshThicknessOptionsByHardware(refs, cfg, state);
    if (urlState.thickness && refs.thickness.querySelector(`option[value="${cssEsc(urlState.thickness)}"]`)) {
      refs.thickness.value = urlState.thickness;
    }
    refreshThicknessExact(refs, cfg);
    applyUrlThicknessExact(refs, urlState);
    refreshBuildStyles(refs, cfg, state.ctx);
    applyUrlBuildStyle(refs, urlState);
    refreshAddons(refs, cfg, state);
    schedulePrice(refs, cfg, state, /*immediate*/ true);

    wireSubmit(form, refs, cfg, state);
  }

  // ── DOM refs ────────────────────────────────────────────────────────────
  function collectRefs(root) {
    const q = (sel) => root.querySelector(sel);
    return {
      root,
      material:            q('[data-cfg="material"]'),
      hardwareList:        q('[data-cfg="hardware-list"]'),
      hardwareNote:        q('[data-cfg="hardware-note"]'),
      depthAdjNote:        q('[data-cfg="depth-adjusted-note"]'),
      finishList:          q('[data-cfg="finish-list"]'),
      finishNote:          q('[data-cfg="finish-note"]'),
      thickness:           q('[data-cfg="thickness"]'),
      thickExactRow:       q('[data-cfg="thickness-exact-row"]'),
      thickExactW:         q('[data-cfg="thickness-exact-w"]'),
      thickExactF:         q('[data-cfg="thickness-exact-f"]'),
      depthW:              q('[data-cfg="depth-w"]'),
      depthF:              q('[data-cfg="depth-f"]'),
      widthW:              q('[data-cfg="width-w"]'),
      widthF:              q('[data-cfg="width-f"]'),
      buildStyles:         q('[data-cfg="build-styles"]'),
      buildNote:           q('[data-cfg="build-note"]'),
      price:               q('[data-cfg="price"]'),
      status:              q('[data-cfg="status"]'),
      atcStatus:           q('[data-cfg="atc-status"]'),
      turnstileWrap:       q('[data-cfg-turnstile]'),
      turnstileWidget:     q('[data-cfg-turnstile-widget]'),
      turnstileErr:        q('[data-cfg-turnstile-err]'),
      atc:                 q('[data-cfg="atc"]'),
      variantId:           q('[data-cfg="variant-id"]'),
      quantity:            q('[data-cfg="quantity"]'),
      hardwareSection:     q('[data-cfg-section="hardware"]'),
      finishSection:       q('[data-cfg-section="finish"]'),
      addons: {
        // Each entry mirrors the data-cfg-addon shortKey ("led" | "puck")
        // and points at the row + controls inside that row. Null entries are
        // tolerated — the JS treats them as opt-in. Custom paint is handled
        // separately by the paint modal, not as a per-shelf addon row.
        led:   collectAddonRefs(root, 'led'),
        puck:  collectAddonRefs(root, 'puck'),
      },
    };
  }

  // Capture the refs for a single addon's UI block. Used by collectRefs().
  // Returns null when the row isn't in the DOM (defensive — different
  // configurator instances may not render every addon).
  function collectAddonRefs(root, shortKey) {
    const row = root.querySelector(`[data-cfg-addon="${shortKey}"]`);
    if (!row) return null;
    return {
      row,
      priceLabel: row.querySelector(`[data-cfg-addon-price="${shortKey}"]`),
      // Per-unit addons (led, puck): qty input + per-unit container.
      qty:        row.querySelector(`[data-cfg-addon-qty="${shortKey}"]`),
      units:      row.querySelector(`[data-cfg-addon-units="${shortKey}"]`),
      // Puck-only: notes textarea + sketch file input + their wrapper row.
      notesRow:   row.querySelector(`[data-cfg-addon-notes-row="${shortKey}"]`),
      notes:      row.querySelector(`[data-cfg-addon-notes="${shortKey}"]`),
      sketch:     row.querySelector(`[data-cfg-addon-sketch="${shortKey}"]`),
      // Flat-fee addons (paint): single text input.
      text:       row.querySelector(`[data-cfg-addon-text="${shortKey}"]`),
    };
  }

  // ── render once ─────────────────────────────────────────────────────────
  function populateAll(refs, cfg, ctx) {
    fillMaterials(refs.material, cfg);
    fillHardware(refs.hardwareList, cfg);
    fillFinish(refs.finishList, cfg);
    fillThickness(refs.thickness, cfg, ctx);
    fillRangeSelect(refs.depthW, depthBoundsFromConfig(cfg).min, depthBoundsFromConfig(cfg).max);
    fillFractions(refs.depthF, cfg.fractions);
    fillRangeSelect(refs.widthW, cfg.widthRange.min, cfg.widthRange.max);
    fillFractions(refs.widthF, cfg.fractions);

    // Sensible defaults so the initial price query targets a realistic combo.
    if (refs.depthW.querySelector('option[value="12"]')) refs.depthW.value = '12';
    if (refs.widthW.querySelector('option[value="24"]')) refs.widthW.value = '24';
  }

  function fillMaterials(select, cfg) {
    const currentName = cfg.currentMaterial && cfg.currentMaterial.name;
    select.innerHTML = cfg.materials.map(m => (
      `<option value="${esc(m.name)}" data-tier="${esc(String(m.tier))}" data-handle="${esc(m.productHandle || '')}" data-rubio="${m.rubioAllowed ? '1' : '0'}"${m.name === currentName ? ' selected' : ''}>${esc(m.name)}</option>`
    )).join('');
  }

  function fillHardware(container, cfg) {
    container.innerHTML = cfg.hardwareOptions.map((opt, i) => (
      `<label class="cfg-chip" data-cfg-hw="${esc(opt.id)}">
         <span>${esc(opt.label)}</span>
         <input type="checkbox" data-cfg-hw-input value="${esc(opt.id)}"${i === 0 ? ' checked' : ''}>
       </label>`
    )).join('');
  }

  function fillFinish(container, cfg) {
    // Rubio Monocoat appears only on Rubio-eligible materials (per Billy: the
    // 7 listed materials, "do not get this option under any circumstances"
    // otherwise). Eligibility is the current page's material — switching
    // material navigates away, so currentMaterial is authoritative here.
    const rubioOk = !!(cfg.currentMaterial && cfg.currentMaterial.rubioAllowed);
    container.innerHTML = cfg.finishOptions
      .filter(opt => !(opt.requiresRubioAllowed && !rubioOk))
      .map((opt) => {
        const cls = 'cfg-chip' + (opt.comingSoon ? ' cfg-chip--coming-soon' : '');
        const disabledAttr = opt.comingSoon ? ' disabled' : '';
        const checkedAttr = opt.isDefault && !opt.comingSoon ? ' checked' : '';
        return (
          `<label class="${cls}" data-cfg-finish="${esc(opt.id)}">
             <span>${esc(opt.label)}</span>
             <input type="checkbox" data-cfg-finish-input value="${esc(opt.id)}"${checkedAttr}${disabledAttr}>
           </label>`
        );
      }).join('');
  }

  function fillThickness(sel, cfg, ctx) {
    // Single discrete dropdown — the DB has rows keyed on the exact label
    // ("0.5\"", "1.75\"", "2.1-4\"", etc.). For "range" thicknesses, a
    // separate "Exact thickness" row appears below (see refreshThicknessExact).
    //
    // Closet mode: Billy's rule is "minimum thickness 1.75" for closet
    // shelves". The thickness data carries a numeric `value` field
    // ("0.5", "1.75", "2.1-4", …). Discrete options (kind=discrete) are
    // filtered by their numeric value; range options are kept when the
    // upper bound (or numeric value) lands at or above the floor, so the
    // 2.1-4 / 4.1-6 ranges still appear.
    //
    // slabNoHwOnly thicknesses (e.g. the 1.25" entry — scope §2.1) are
    // filtered out unless the current hardware mode allows them. The bottom
    // "Slab No Hardware" wrapper block sets ctx.slabNoHwMode = true so 1.25"
    // is always available there; the top section toggles ctx.slabNoHwActive
    // based on the current Hardware chip.
    let thicknesses = cfg.thicknesses.slice();
    if (ctx && ctx.closetMode) {
      thicknesses = thicknesses.filter(t => {
        const max = (t.maxInches != null) ? parseFloat(t.maxInches) : parseFloat(t.value);
        return Number.isFinite(max) && max >= 1.75;
      });
    }
    const slabNoHwOK = !!(ctx && (ctx.slabNoHwMode || ctx.slabNoHwActive));
    if (!slabNoHwOK) {
      thicknesses = thicknesses.filter(t => !t.slabNoHwOnly);
      // Billy 2026-07-28: remove the thin thicknesses (< 1.75") from the
      // FLOATING (and any non-slab) dropdown entirely — they were causing
      // confusion. They remain available only on Slab — No Hardware, which
      // is where thin orders belong. Range thicknesses ("2.1-4" → parseFloat
      // 2.1) and 1.75"+ are kept; a non-numeric value never gets dropped.
      thicknesses = thicknesses.filter(t => {
        const n = parseFloat(t.value);
        return !Number.isFinite(n) || n >= 1.75;
      });
    }

    // Numeric sort so a synthetic entry like 1.25 lands between 1 and 1.5
    // in the dropdown, rather than at the tail. Range thicknesses ("2.1-4",
    // "4.1-6") sort by their lower bound which keeps them after the
    // discrete entries — the existing ordering convention.
    thicknesses.sort((a, b) => {
      const aN = parseFloat(a.value);
      const bN = parseFloat(b.value);
      return (Number.isFinite(aN) ? aN : 99) - (Number.isFinite(bN) ? bN : 99);
    });

    sel.innerHTML = thicknesses.map(t => {
      // Pricing redirect: when an entry's pricingValue differs from its
      // own value, we send the redirected label to the backend so the DB
      // lookup hits the correct row. The redirect target is the DB label
      // (label form, with the trailing inch-mark) of the entry whose
      // value matches pricingValue. Falls back to the option's own label
      // when there's no redirect — preserves existing behavior for all
      // metaobject-defined entries.
      const redirect = (t.pricingValue && t.pricingValue !== t.value)
        ? findThicknessLabel(cfg, t.pricingValue)
        : null;
      const pricingAttr = redirect ? ` data-pricing="${esc(redirect)}"` : '';
      return `<option value="${esc(t.label)}" data-kind="${esc(t.kind)}" data-numeric="${esc(t.value)}"${pricingAttr}${t.maxInches != null ? ` data-max="${t.maxInches}"` : ''}>${esc(t.label)}</option>`;
    }).join('');
    const def = thicknesses.find(t => t.value === '1.75') || thicknesses[0];
    if (def) sel.value = def.label;
  }

  // Resolve a thickness `value` (e.g. "1.5") to its rendered label
  // (e.g. `1.5"`) by looking up the metaobject entry whose value matches.
  // Used by the pricing-redirect path so the synthetic 1.25" option can
  // send the actual DB row label ("1.5\"") to /api/price even though
  // pricingValue stored as the bare numeric is the natural metaobject
  // convention.
  function findThicknessLabel(cfg, value) {
    const hit = (cfg.thicknesses || []).find(t => t.value === value);
    return hit ? hit.label : null;
  }

  // Re-render the thickness dropdown when the current Hardware selection
  // changes. Slab No Hardware unlocks the 1.25" option; switching back to
  // Floating Shelf w/ Hardware drops it (and snaps the selection to 1.75"
  // if 1.25" was active). Defensive — preserves the prior pick if still
  // valid in the new list.
  function refreshThicknessOptionsByHardware(refs, cfg, state) {
    if (!refs.thickness) return;
    const hwOpt = currentHardwareOption(refs, cfg);
    const slabNoHwActive = !!(hwOpt && hwOpt.hardware === 'no');
    const ctx = Object.assign({}, state.ctx || {}, { slabNoHwActive });
    const prev = refs.thickness.value;
    fillThickness(refs.thickness, cfg, ctx);
    if (prev && refs.thickness.querySelector(`option[value="${cssEsc(prev)}"]`)) {
      refs.thickness.value = prev;
    }
  }

  function fillRangeSelect(select, min, max) {
    const prev = select.value;
    const out = [];
    for (let i = Math.ceil(min); i <= Math.floor(max); i++) out.push(`<option value="${i}">${i}</option>`);
    select.innerHTML = out.join('');
    // Preserve prior selection if still in range, otherwise clamp to the new max.
    if (prev && select.querySelector(`option[value="${prev}"]`)) {
      select.value = prev;
    } else if (select.options.length) {
      select.value = String(Math.floor(max));
    }
  }

  function fillFractions(select, fractions) {
    select.innerHTML = fractions.map(f => `<option value="${esc(f)}">${esc(f === '0' ? '0' : f)}</option>`).join('');
  }

  function depthBoundsFromConfig(cfg) {
    const mins = cfg.depthRanges.map(d => Math.ceil(d.min));
    const maxs = cfg.depthRanges.map(d => Math.floor(d.max));
    return { min: Math.min(...mins), max: Math.max(...maxs) };
  }

  // ── per-product overrides ──────────────────────────────────────────────
  // Applied after populateAll so the controls exist; runs before URL-state
  // restoration so the locked hardware value isn't clobbered by a stale URL.
  function applyPerProductOverrides(refs, cfg, ctx) {
    if (cfg.lockMaterial && refs.material.value) refs.material.disabled = true;
    if (cfg.hideHardware && refs.hardwareSection) refs.hardwareSection.hidden = true;
    if (cfg.hideFinish   && refs.finishSection)   refs.finishSection.hidden   = true;

    // Per-root hardware lock (data-locked-hardware on <slab-configurator>).
    // The Hardware row hides and the matching chip is force-selected so the
    // existing gating logic (depth ceiling, build-style filtering, /api/price
    // payload) sees the locked value as if the customer chose it.
    if (ctx && ctx.lockedHardware && refs.hardwareSection && refs.hardwareList) {
      refs.hardwareSection.hidden = true;
      refs.hardwareList.querySelectorAll('[data-cfg-hw-input]').forEach(el => {
        el.checked = (el.value === ctx.lockedHardware);
      });
    }
  }

  // ── URL state persistence (ported from pilot) ───────────────────────────
  // The full configuration is mirrored in the page URL via history.replaceState
  // so reloads, shares, and browser back/forward restore selections + price
  // instead of resetting to the default minimum. Material is NOT persisted —
  // it's implicit in the product page (each material is its own page).
  const PARAM_MAP = {
    hardware:   'hw',   // hardware option id (floating|slab)
    finish:     'f',    // finish chip id
    thickness:  't',    // thickness label
    thickWhole: 'tw',   // exact-thickness whole (range thicknesses only)
    thickFrac:  'tf',   // exact-thickness fraction
    depthWhole: 'dw',
    depthFrac:  'df',
    widthWhole: 'ww',
    widthFrac:  'wf',
    buildStyle: 'bs',
  };

  function snapshotControls(refs) {
    const hw    = refs.hardwareList.querySelector('[data-cfg-hw-input]:checked');
    const fin   = refs.finishList.querySelector('[data-cfg-finish-input]:checked');
    const build = refs.buildStyles && refs.buildStyles.querySelector('input[name="cfg-build-style"]:checked');
    const tOpt  = refs.thickness.options[refs.thickness.selectedIndex];
    const isRange = tOpt && tOpt.dataset.kind === 'range';
    return {
      hardware:   hw  ? hw.value  : null,
      finish:     fin ? fin.value : null,
      thickness:  refs.thickness.value,
      thickWhole: (isRange && refs.thickExactW) ? refs.thickExactW.value : null,
      thickFrac:  (isRange && refs.thickExactF) ? refs.thickExactF.value : null,
      depthWhole: refs.depthW.value,
      depthFrac:  refs.depthF.value,
      widthWhole: refs.widthW.value,
      widthFrac:  refs.widthF.value,
      buildStyle: build ? build.value : null,
    };
  }

  // `ctx.urlPrefix` lets multiple slab instances share the same URL without
  // overwriting each other's keys: e.g. the top "with hardware" section uses
  // `t/dw/df/...` (no prefix — preserves all pre-split share links), while
  // the bottom "no-hardware" section uses `nh_t/nh_dw/nh_df/...`.
  function paramKey(ctx, key) {
    return (ctx && ctx.urlPrefix ? ctx.urlPrefix : '') + PARAM_MAP[key];
  }

  function readUrlState(ctx) {
    const p = new URLSearchParams(window.location.search);
    const out = {};
    Object.keys(PARAM_MAP).forEach(k => {
      const v = p.get(paramKey(ctx, k));
      if (v !== null && v !== '') out[k] = v;
    });
    return out;
  }

  function writeUrlState(refs, ctx) {
    const p = new URLSearchParams(window.location.search);
    const snap = snapshotControls(refs);
    Object.keys(PARAM_MAP).forEach(k => {
      const urlKey = paramKey(ctx, k);
      if (snap[k] != null && snap[k] !== '') p.set(urlKey, snap[k]);
      else p.delete(urlKey);
    });
    const qs = p.toString();
    // replaceState (not push) — the back button should leave the page, not
    // unwind dozens of intermediate configuration states. Preserve any
    // #cfg-* section hash (deep-link from a category collection) so it isn't
    // stripped and the section re-scroll keeps working / stays shareable.
    history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + (window.location.hash || ''));
  }

  function setSelectByValue(sel, value) {
    if (!sel || value == null) return false;
    for (let i = 0; i < sel.options.length; i++) {
      if (sel.options[i].value === value) { sel.value = value; return true; }
    }
    return false;
  }

  function checkExclusive(container, selector, value) {
    if (!container || value == null) return false;
    const inputs = container.querySelectorAll(selector);
    let target = null;
    inputs.forEach(el => { if (el.value === value) target = el; });
    if (!target || target.disabled) return false;
    inputs.forEach(el => { el.checked = (el === target); });
    return true;
  }

  // Restore the raw control values that exist immediately after populateAll.
  // Finish / exact-thickness / build-style are restored later, once their
  // dynamic refresh has rendered the relevant controls.
  function applyUrlBasicControls(refs, s) {
    checkExclusive(refs.hardwareList, '[data-cfg-hw-input]', s.hardware);
    setSelectByValue(refs.thickness, s.thickness);
    setSelectByValue(refs.depthW, s.depthWhole);
    setSelectByValue(refs.depthF, s.depthFrac);
    setSelectByValue(refs.widthW, s.widthWhole);
    setSelectByValue(refs.widthF, s.widthFrac);
  }

  function applyUrlFinish(refs, s) {
    if (checkExclusive(refs.finishList, '[data-cfg-finish-input]', s.finish)) hideFinishNote(refs);
  }

  function applyUrlThicknessExact(refs, s) {
    if (!refs.thickExactRow || refs.thickExactRow.hidden) return;
    setSelectByValue(refs.thickExactW, s.thickWhole);
    setSelectByValue(refs.thickExactF, s.thickFrac);
  }

  function applyUrlBuildStyle(refs, s) {
    if (!refs.buildStyles) return;
    checkExclusive(refs.buildStyles, 'input[name="cfg-build-style"]', s.buildStyle);
  }

  // ── material switch → full page navigation ──────────────────────────────
  // Carries the current configuration across to the destination material's
  // product page via the URL. Drops Rubio if the destination isn't eligible.
  function navigateToMaterial(refs, cfg, state, materialName) {
    const target = cfg.materials.find(m => m.name === materialName);
    if (!target) return;
    writeUrlState(refs, state.ctx);
    const params = new URLSearchParams(window.location.search);
    if (params.get('f') === 'rubio-monocoat' && !target.rubioAllowed) params.delete('f');
    const path = target.productUrl
      ? target.productUrl.split('?')[0]
      : `${shopifyRoot()}products/${target.productHandle}`;
    const qs = params.toString();
    window.location.href = path + (qs ? `?${qs}` : '');
  }

  // ── events ──────────────────────────────────────────────────────────────
  function wireEvents(refs, cfg, state) {
    refs.material.addEventListener('change', () => {
      // Each material is its own product page. Changing the material navigates
      // to that product, carrying the rest of the configuration in the URL so
      // the destination page restores it. The cart line then shows the correct
      // product name + image (Billy's QA item) because the host product really
      // changes — no client-side product swapping.
      const selectedName = refs.material.value;
      const currentName = cfg.currentMaterial && cfg.currentMaterial.name;
      if (selectedName && selectedName !== currentName) {
        navigateToMaterial(refs, cfg, state, selectedName);
        return;
      }
      // Same material selected (rare) — keep the in-page refresh path.
      clearStatus(refs);
      hideDepthAdjustNote(refs);
      const lockedToOverride = applyMaterialFinishOverrideIfNeeded(refs, cfg);
      if (!lockedToOverride) refreshFinishAvailability(refs, cfg, /*silent*/ false);
      refreshAddons(refs, cfg, state);
      schedulePrice(refs, cfg, state);
    });

    refs.hardwareList.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cfg-hw-input]');
      if (!input || input.disabled) return;
      refs.hardwareList.querySelectorAll('[data-cfg-hw-input]').forEach(el => {
        if (el !== input) el.checked = false;
      });
      if (!input.checked) input.checked = true;
      const clampedFromAbove = refreshDepthOptionsByHardware(refs, cfg);
      applyDimCeilingRules(refs);
      // Slab No Hardware unlocks 1.25", Floating Shelf w/ Hardware hides
      // it again. Refresh before downstream gating so build-styles see the
      // freshly resolved thickness.
      refreshThicknessOptionsByHardware(refs, cfg, state);
      refreshThicknessExact(refs, cfg);
      refreshBuildStyles(refs, cfg, state.ctx);
      refreshAddons(refs, cfg, state);
      if (clampedFromAbove) {
        showDepthAdjustNote(refs, 'Depth adjusted to 16″ — maximum for Shelf with Hardware.');
      } else {
        hideDepthAdjustNote(refs);
      }
      schedulePrice(refs, cfg, state);
    });

    refs.finishList.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cfg-finish-input]');
      if (!input || input.disabled) return;
      refs.finishList.querySelectorAll('[data-cfg-finish-input]').forEach(el => {
        if (el !== input) el.checked = false;
      });
      if (!input.checked) input.checked = true;
      // Customer made a deliberate finish pick — any prior auto-switch note is
      // no longer relevant.
      hideFinishNote(refs);
      schedulePrice(refs, cfg, state);
    });

    refs.thickness.addEventListener('change', () => {
      refreshThicknessExact(refs, cfg);
      refreshBuildStyles(refs, cfg, state.ctx);
      refreshAddons(refs, cfg, state);
      schedulePrice(refs, cfg, state);
    });

    if (refs.thickExactW) {
      refs.thickExactW.addEventListener('change', () => {
        const tEntry = cfg.thicknesses.find(t => t.label === refs.thickness.value);
        if (tEntry && tEntry.maxInches != null) {
          const minWhole = Math.floor(parseFloat(tEntry.value));
          const maxWhole = Math.floor(tEntry.maxInches);
          applyDimCeilingRule(refs.thickExactW, refs.thickExactF, minWhole, maxWhole, /*floorExclusive*/ true);
        }
        schedulePrice(refs, cfg, state);
      });
    }
    if (refs.thickExactF) {
      refs.thickExactF.addEventListener('change', () => schedulePrice(refs, cfg, state));
    }

    [refs.depthW, refs.depthF].forEach(el => {
      el.addEventListener('change', () => {
        hideDepthAdjustNote(refs);
        applyDimCeilingRules(refs);
        refreshHardwareByDepth(refs, cfg);
        // Depth gates the Narrow build style (hardware=yes, depth ≤ 6″).
        refreshBuildStyles(refs, cfg, state.ctx);
        schedulePrice(refs, cfg, state);
      });
    });

    [refs.widthW, refs.widthF].forEach(el => {
      el.addEventListener('change', () => {
        applyDimCeilingRules(refs);
        schedulePrice(refs, cfg, state);
      });
    });

    if (refs.buildStyles) {
      refs.buildStyles.addEventListener('change', (e) => {
        const input = e.target.closest('input[name="cfg-build-style"]');
        if (!input || input.disabled) return;
        // Build style is informational only — no price refetch needed, but we
        // still bump the lastPriceKey check so the cart-line stays in sync.
        state.lastPriceKey = null;
        schedulePrice(refs, cfg, state, /*immediate*/ true);
      });
    }
  }

  // ── dynamic gating: material → finish ──────────────────────────────────
  // When the active finish becomes invalid for the new material, switch
  // automatically to the default (Clear Matte) and (unless silent) surface a
  // transient note. Materials with a locked single-chip finish are handled
  // upstream by applyMaterialFinishOverrideIfNeeded().
  function refreshFinishAvailability(refs, cfg, silent) {
    // Materials with a locked single-chip finish (Painted White / Primed /
    // U-Paint MDF) own the finish list — bail so we don't clobber the chip.
    if (currentMaterialOverride(refs, cfg)) {
      hideFinishNote(refs);
      return;
    }

    const tier = currentTier(refs);
    const rule = cfg.tierRules[String(tier)] || { finished: true, unfinished: true };

    // Re-render the standard finish list (in case we were just rendering a
    // locked override chip from another material).
    if (!refs.finishList.querySelector('[data-cfg-finish]') || isOverrideListRendered(refs, cfg)) {
      fillFinish(refs.finishList, cfg);
    }

    const prevSelected = refs.finishList.querySelector('[data-cfg-finish-input]:checked');
    const prevId = prevSelected ? prevSelected.value : null;

    refs.finishList.querySelectorAll('[data-cfg-finish]').forEach(label => {
      const id = label.dataset.cfgFinish;
      const opt = cfg.finishOptions.find(o => o.id === id);
      if (!opt) return;
      // Rubio is priced off the finished CV base, so it follows rule.finished.
      const isFinishedFamily = opt.finish === 'finished' || opt.finish === 'rubio';
      const allowedByRule = isFinishedFamily ? rule.finished : rule.unfinished;
      const allowed = allowedByRule && !opt.comingSoon;
      label.classList.toggle('cfg-chip--unavailable', !allowedByRule);
      const input = label.querySelector('input');
      input.disabled = !allowed;
      if (!allowed && input.checked) input.checked = false;
    });

    let switched = false;
    if (!refs.finishList.querySelector('input:checked')) {
      // Prefer Clear Matte (the isDefault entry) when available — Billy's spec.
      const defaultOpt = cfg.finishOptions.find(o => o.isDefault && !o.comingSoon);
      const defaultInput = defaultOpt
        ? refs.finishList.querySelector(`[data-cfg-finish="${defaultOpt.id}"] input:not([disabled])`)
        : null;
      const fallback = defaultInput || refs.finishList.querySelector('input:not([disabled])');
      if (fallback) {
        fallback.checked = true;
        switched = true;
      }
    }

    if (switched && !silent) {
      const newLabel = refs.finishList.querySelector('[data-cfg-finish-input]:checked')
        .closest('[data-cfg-finish]').querySelector('span').textContent;
      // Persistent: the customer didn't initiate this change — the note must
      // stay readable until their next deliberate input clears it.
      showFinishNote(
        refs,
        `Raw/Unfinished is not available for ${currentMaterialName(refs)} — switched to “${newLabel}”.`,
        /*persistent*/ true
      );
    } else if (!switched) {
      hideFinishNote(refs);
    }
  }

  function isOverrideListRendered(refs, cfg) {
    const overrides = cfg.materialFinishOverrides || {};
    for (const k in overrides) {
      const id = overrides[k].id;
      if (refs.finishList.querySelector(`[data-cfg-finish="${cssEsc(id)}"]`)) return true;
    }
    return false;
  }

  function showFinishNote(refs, text, persistent) {
    if (!refs.finishNote) return;
    refs.finishNote.textContent = text;
    refs.finishNote.hidden = false;
    refs.finishNote.classList.remove('cfg-finish-note--hide');
    clearTimeout(refs.finishNote.__hideTimer);
    if (persistent) return;
    refs.finishNote.__hideTimer = setTimeout(() => {
      refs.finishNote.classList.add('cfg-finish-note--hide');
    }, FINISH_NOTE_TTL_MS);
  }

  function hideFinishNote(refs) {
    if (!refs.finishNote) return;
    refs.finishNote.hidden = true;
    refs.finishNote.textContent = '';
    clearTimeout(refs.finishNote.__hideTimer);
  }

  // ── Material → locked finish override ───────────────────────────────────
  // Materials listed in cfg.materialFinishOverrides collapse the finish
  // selector to a single locked chip. The chip's underlying finish (finished /
  // unfinished) is still sent to the API; the line item property records the
  // chip label.
  function applyMaterialFinishOverrideIfNeeded(refs, cfg, silent) {
    const overrides = cfg.materialFinishOverrides || {};
    const name = currentMaterialName(refs);
    const override = overrides[name];
    if (!override) return false;

    // No transition note when we're already locked to this exact override.
    const alreadyLocked = !!refs.finishList.querySelector(`[data-cfg-finish="${cssEsc(override.id)}"]`);

    refs.finishList.innerHTML = (
      `<label class="cfg-chip cfg-chip--locked" data-cfg-finish="${esc(override.id)}">
         <span>${esc(override.label)}</span>
         <input type="checkbox" data-cfg-finish-input value="${esc(override.id)}" checked disabled>
       </label>`
    );

    // The override note describes a permanent state of the form (the chip stays
    // locked as long as this material is selected), so keep it visible — not
    // transient like the Raw/Unfinished → Clear Matte auto-switch note.
    if (override.note) {
      showFinishNote(refs, override.note, /*persistent*/ true);
    } else {
      hideFinishNote(refs);
    }
    return true;
  }

  function currentMaterialOverride(refs, cfg) {
    const overrides = cfg.materialFinishOverrides || {};
    return overrides[currentMaterialName(refs)] || null;
  }

  // ── status / depth-adjust note helpers ──────────────────────────────────
  function clearStatus(refs) {
    if (!refs.status) return;
    refs.status.textContent = '';
    refs.status.hidden = true;
  }

  function showDepthAdjustNote(refs, text) {
    if (!refs.depthAdjNote) return;
    refs.depthAdjNote.textContent = text;
    refs.depthAdjNote.hidden = false;
    clearTimeout(refs.depthAdjNote.__hideTimer);
    refs.depthAdjNote.__hideTimer = setTimeout(() => hideDepthAdjustNote(refs), FINISH_NOTE_TTL_MS);
  }

  function hideDepthAdjustNote(refs) {
    if (!refs.depthAdjNote) return;
    refs.depthAdjNote.hidden = true;
    refs.depthAdjNote.textContent = '';
    clearTimeout(refs.depthAdjNote.__hideTimer);
  }

  // ── dynamic gating: depth → hardware ────────────────────────────────────
  // If the current depth resolves to a range with hardware_eligible=false,
  // disable the hardware=yes options and auto-switch to a valid one.
  function refreshHardwareByDepth(refs, cfg) {
    const range = resolveDepthRange(refs.depthW.value, refs.depthF.value, cfg);
    const eligible = !!(range && range.hwEligible);
    let switched = false;

    cfg.hardwareOptions.forEach(opt => {
      const input = refs.hardwareList.querySelector(`[data-cfg-hw-input][value="${opt.id}"]`);
      const label = refs.hardwareList.querySelector(`[data-cfg-hw="${opt.id}"]`);
      if (!input || !label) return;

      const requiresEligible = opt.hardware === 'yes';
      const disabled = requiresEligible && !eligible;

      input.disabled = disabled;
      label.classList.toggle('cfg-chip--unavailable', disabled);

      if (disabled && input.checked) {
        input.checked = false;
        switched = true;
      }
    });

    if (switched && !refs.hardwareList.querySelector('[data-cfg-hw-input]:checked')) {
      const fallback = refs.hardwareList.querySelector('[data-cfg-hw-input]:not([disabled])');
      if (fallback) {
        fallback.checked = true;
        // Hardware just auto-switched → its dependent UI must catch up.
        refreshDepthOptionsByHardware(refs, cfg);
        applyDimCeilingRules(refs);
        refreshBuildStyles(refs, cfg, state.ctx);
      }
    }

    // Show the depth-cap note only when the hardware=yes chip is actually
    // disabled (i.e. the user's current depth would prevent picking it).
    // Suppresses it for users already on hw=no with deep slabs, where the
    // hardware constraint is irrelevant context.
    const hwYesInput = refs.hardwareList.querySelector('[data-cfg-hw-input][value="floating"]');
    if (refs.hardwareNote) refs.hardwareNote.hidden = !(hwYesInput && hwYesInput.disabled);
  }

  // ── dynamic gating: hardware → depth ceiling ───────────────────────────
  // When the customer selects Shelf w/ Hardware first, the depth dropdown
  // shrinks to 3-16 (per hwOpt.maxDepth). Outside that path the full 3-28
  // range is exposed. Returns true when a prior depth above the new ceiling
  // was clamped down — caller can surface a "depth adjusted" note.
  function refreshDepthOptionsByHardware(refs, cfg) {
    const hwOpt = currentHardwareOption(refs, cfg);
    const maxDepth = (hwOpt && typeof hwOpt.maxDepth === 'number')
      ? hwOpt.maxDepth
      : depthBoundsFromConfig(cfg).max;
    const minDepth = depthBoundsFromConfig(cfg).min;
    const prev = parseInt(refs.depthW.value, 10);
    fillRangeSelect(refs.depthW, minDepth, maxDepth);
    const next = parseInt(refs.depthW.value, 10);
    return Number.isFinite(prev) && Number.isFinite(next) && prev > maxDepth && next !== prev;
  }

  // ── exact-thickness row (appears for range types like 2.1-4", 4.1-6") ──
  function refreshThicknessExact(refs, cfg) {
    if (!refs.thickExactRow) return;
    const opt = refs.thickness.options[refs.thickness.selectedIndex];
    if (!opt || opt.dataset.kind !== 'range') {
      refs.thickExactRow.hidden = true;
      return;
    }

    const tEntry = cfg.thicknesses.find(t => t.label === refs.thickness.value);
    if (!tEntry || tEntry.maxInches == null) {
      refs.thickExactRow.hidden = true;
      return;
    }

    const minWhole = Math.floor(parseFloat(tEntry.value));
    const maxWhole = Math.floor(tEntry.maxInches);

    // Reset to the range floor (minWhole + 1/16″, since "2.1-4" excludes 2.0
    // exactly) whenever the customer switches to a different range thickness.
    // Without this the dropdown defaults to maxWhole (fillRangeSelect's
    // fresh-fill default), which made "2.1–4″" land on 4″.
    const labelChanged = refs.thickExactRow.dataset.cfgActive !== tEntry.label;

    fillRangeSelect(refs.thickExactW, minWhole, maxWhole);
    fillFractions(refs.thickExactF, cfg.fractions);

    if (labelChanged || !isInRange(refs.thickExactW.value, minWhole, maxWhole)) {
      refs.thickExactW.value = String(minWhole);
      refs.thickExactF.value = '1/16';
    }
    refs.thickExactRow.dataset.cfgActive = tEntry.label;
    applyDimCeilingRule(refs.thickExactW, refs.thickExactF, minWhole, maxWhole, /*floorExclusive*/ true);
    refs.thickExactRow.hidden = false;
  }

  // ── dim ceiling rules (depth + width + exact-thickness) ────────────────
  // Generic version of the pilot's thickness floor/ceiling logic:
  // - at the ceiling whole value → fraction is locked to "0"
  // - at the floor whole value   → fraction "0" is disabled (would dip below the range min)
  function applyDimCeilingRules(refs) {
    const dW = parseInt(refs.depthW.value, 10);
    const wW = parseInt(refs.widthW.value, 10);
    const depthMax = parseInt(refs.depthW.options[refs.depthW.options.length - 1]?.value, 10);
    const depthMin = parseInt(refs.depthW.options[0]?.value, 10);
    const widthMax = parseInt(refs.widthW.options[refs.widthW.options.length - 1]?.value, 10);
    const widthMin = parseInt(refs.widthW.options[0]?.value, 10);

    if (Number.isFinite(depthMax) && Number.isFinite(depthMin)) {
      applyDimCeilingRule(refs.depthW, refs.depthF, depthMin, depthMax, /*floorExclusive*/ false);
    }
    if (Number.isFinite(widthMax) && Number.isFinite(widthMin)) {
      applyDimCeilingRule(refs.widthW, refs.widthF, widthMin, widthMax, /*floorExclusive*/ false);
    }
  }

  // applyDimCeilingRule(wholeSel, fracSel, minWhole, maxWhole, floorExclusive)
  // - At the ceiling: locks fraction to "0" and disables it.
  // - At the floor: only locks out "0" if `floorExclusive` is true (the
  //   thickness-exact case — e.g. "2.1-4" means whole=2 + fraction=0 → 2.0 is
  //   below the lower bound 2.1). Depth/width have integer minimums (3, 9) so
  //   pass floorExclusive=false there.
  function applyDimCeilingRule(wholeSel, fracSel, minWhole, maxWhole, floorExclusive) {
    if (!wholeSel || !fracSel) return;
    const whole = wholeSel.value;
    const zeroOpt = fracSel.querySelector('option[value="0"]');

    if (whole === String(maxWhole)) {
      fracSel.value = '0';
      fracSel.disabled = true;
      if (zeroOpt) zeroOpt.disabled = false;
    } else if (floorExclusive && whole === String(minWhole)) {
      fracSel.disabled = false;
      if (zeroOpt) zeroOpt.disabled = true;
      if (fracSel.value === '0') fracSel.value = '1/16';
    } else {
      fracSel.disabled = false;
      if (zeroOpt) zeroOpt.disabled = false;
    }
  }

  function isInRange(v, min, max) {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= min && n <= max;
  }

  // ── build styles ────────────────────────────────────────────────────────
  // Rebuilt on hardware change, thickness change, AND depth change. Filtering:
  //   hardware=yes:
  //     depth ≤ 6″ → only the narrowOnly entry is visible (Narrow)
  //     depth > 6″ → narrowOnly hidden, the rest (Standard / WC1E / WC2E) shown
  //   hardware=no:
  //     thickness < 1.75″ → entries with minThicknessInches filtered out
  //                         (leaves only Slab no HW)
  //     thickness ≥ 1.75″ → all hw=no entries shown
  // Build styles allowed for closet shelves (Billy 2026-06-07): WC3S
  // (default) and Split Assembly. Both already exist in cfg.buildStyles
  // under hardware='no'; we keep them and hide the "Slab no HW" chip
  // since the customer is already in the dedicated Closet section.
  const CLOSET_BUILD_STYLE_IDS = ['wc3s', 'split'];

  // Slab — No Hardware section: Billy's launch ask is to show only the
  // "Slab no HW" chip and hide WC3S + Split Assembly (those moved to
  // Niche / Closet). Same mechanism as closet, just a different subset.
  const SLAB_NO_HW_BUILD_STYLE_IDS = ['slab'];

  function refreshBuildStyles(refs, cfg, ctx) {
    if (!refs.buildStyles || !Array.isArray(cfg.buildStyles)) return;

    const hwOpt = currentHardwareOption(refs, cfg);
    const hwId = hwOpt ? hwOpt.hardware : null;
    const tNumeric = currentThicknessNumeric(refs);
    const depthRange = resolveDepthRange(refs.depthW.value, refs.depthF.value, cfg);
    // "Narrow regime": Floating Shelf w/ Hardware at depth ≤ 6″.
    // We key off the depth-range data so a future split (e.g. 3-5 / 5.1-6) keeps
    // working without a JS edit — any range whose max ≤ 6 triggers narrow mode.
    const narrowRegime = hwId === 'yes' && !!(depthRange && depthRange.max <= 6);

    const isCloset    = !!(ctx && ctx.closetMode);
    const isSlabNoHw  = !!(ctx && ctx.slabNoHwMode);

    const prevSelectedId = (refs.buildStyles.querySelector('input[name="cfg-build-style"]:checked') || {}).value || null;

    const visible = cfg.buildStyles.filter(s => {
      if (s.hardware && hwId && s.hardware !== hwId) return false;
      // Closet section: restrict to WC3S + Split Assembly only.
      if (isCloset && !CLOSET_BUILD_STYLE_IDS.includes(s.id)) return false;
      // Slab — No Hardware section: only the "Slab no HW" chip is allowed.
      if (isSlabNoHw && !SLAB_NO_HW_BUILD_STYLE_IDS.includes(s.id)) return false;
      // Narrow-only entries appear ONLY in the narrow regime.
      if (s.narrowOnly && !narrowRegime) return false;
      // In the narrow regime, hide all non-narrow hardware=yes entries.
      if (narrowRegime && s.hardware === 'yes' && !s.narrowOnly) return false;
      if (typeof s.minThicknessInches === 'number' && tNumeric != null && tNumeric < s.minThicknessInches) {
        return false;
      }
      return true;
    });

    if (!visible.length) {
      refs.buildStyles.innerHTML = '';
      if (refs.buildNote) {
        refs.buildNote.hidden = false;
        refs.buildNote.textContent = 'No build styles available for this selection.';
      }
      return;
    }
    if (refs.buildNote) refs.buildNote.hidden = true;

    // Pick the default: preserve the prior choice if still visible, else use
    // the explicit isDefault entry, else fall back to the first visible item.
    // Closet section overrides this — Billy picked WC3S as the default chip.
    let defaultId = (visible.find(s => s.id === prevSelectedId) || {}).id;
    if (!defaultId && isCloset) defaultId = (visible.find(s => s.id === 'wc3s') || visible[0]).id;
    if (!defaultId) defaultId = (visible.find(s => s.isDefault) || visible[0]).id;

    // Per-section input id so the explicit `for=` association picks the radio,
    // not the eye <button>. Without `for=`, the label activates the *first
    // labelable descendant in tree order* — and <button> is labelable — so
    // clicks on the image area would open the modal instead of toggling the
    // radio. The `for=` makes the labeled control unambiguous.
    const idPrefix = `cfg-build-${cfg.sectionId || 'x'}`;

    refs.buildStyles.innerHTML = visible.map(s => {
      const inputId = `${idPrefix}-${s.id}`;
      return (
        `<label class="cfg-build" data-cfg-build="${esc(s.id)}" for="${esc(inputId)}">
           <span class="cfg-build__box">
             <img class="cfg-build__img" src="${esc(s.image || '')}" alt="${esc(s.label)}" onerror="this.classList.add('cfg-build__img--missing')">
             <button type="button" class="cfg-build__zoom" data-cfg-zoom="${esc(s.image || '')}" data-cfg-zoom-label="${esc(s.label)}" aria-label="View ${esc(s.label)} larger" title="View larger">
               <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                 <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                 <circle cx="12" cy="12" r="3"></circle>
               </svg>
             </button>
           </span>
           <span class="cfg-build__check">
             <input type="radio" id="${esc(inputId)}" name="cfg-build-style" value="${esc(s.id)}"${s.id === defaultId ? ' checked' : ''}>
             <span class="cfg-build__indicator" aria-hidden="true"></span>
             <span class="cfg-build__name">${esc(s.label)}</span>
           </span>
         </label>`
      );
    }).join('');
  }

  // ── build-style image modal ─────────────────────────────────────────────
  // One modal element appended to body; the eye button on each tile flips it
  // open with the matching image. Close via the X, the backdrop, or Esc.
  function wireBuildStyleModal(refs) {
    if (!refs.buildStyles) return;

    let modal = document.querySelector('[data-cfg-modal]');
    if (!modal) {
      modal = document.createElement('div');
      modal.className = 'cfg-modal';
      modal.setAttribute('data-cfg-modal', '');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', 'Build style preview');
      modal.hidden = true;
      modal.innerHTML = (
        `<div class="cfg-modal__backdrop" data-cfg-modal-close></div>
         <div class="cfg-modal__dialog">
           <button type="button" class="cfg-modal__close" data-cfg-modal-close aria-label="Close">
             <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
               <line x1="6" y1="6" x2="18" y2="18"></line>
               <line x1="18" y1="6" x2="6" y2="18"></line>
             </svg>
           </button>
           <img class="cfg-modal__img" alt="">
           <p class="cfg-modal__caption"></p>
         </div>`
      );
      document.body.appendChild(modal);

      modal.addEventListener('click', (e) => {
        if (e.target.closest('[data-cfg-modal-close]')) closeBuildStyleModal(modal);
      });
    }

    refs.buildStyles.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cfg-zoom]');
      if (!btn) return;
      // `<button type="button">` inside a label doesn't activate the radio,
      // but we still call preventDefault to be defensive against future
      // tweaks (e.g. wrapping in a form).
      e.preventDefault();
      openBuildStyleModal(modal, btn.dataset.cfgZoom, btn.dataset.cfgZoomLabel || '');
    });
  }

  function openBuildStyleModal(modal, src, label) {
    if (!modal || !src) return;
    const img = modal.querySelector('.cfg-modal__img');
    const cap = modal.querySelector('.cfg-modal__caption');
    img.src = src;
    img.alt = label;
    cap.textContent = label;
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    modal.__cfgKeyHandler = (e) => { if (e.key === 'Escape') closeBuildStyleModal(modal); };
    document.addEventListener('keydown', modal.__cfgKeyHandler);
    modal.querySelector('.cfg-modal__close').focus();
  }

  function closeBuildStyleModal(modal) {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    const img = modal.querySelector('.cfg-modal__img');
    img.removeAttribute('src');
    document.documentElement.style.overflow = '';
    if (modal.__cfgKeyHandler) {
      document.removeEventListener('keydown', modal.__cfgKeyHandler);
      modal.__cfgKeyHandler = null;
    }
  }

  // ── price ───────────────────────────────────────────────────────────────
  // ── Add-ons ─────────────────────────────────────────────────────────────
  //
  // The catalog lives in window.ADDONS_CONFIG (emitted by snippets/addons-
  // config.liquid from the shop metafield). Each addon entry carries its
  // price, unit, eligibility rules, and per-unit input schema. The
  // configurator never bakes addon prices or rules into the JS — when
  // Billy edits the addon_pricing CSV, the next page load picks up the
  // new prices through the metafield.
  //
  // SELECTION_SHORT_KEYS mirrors the backend mapping (src/services/
  // addon.service.js#SELECTION_KEYS). Keep these in sync if the catalog
  // grows.
  const SELECTION_SHORT_KEYS = {
    led_channel_prep:   'led',
    puck_light_hole:    'puck',
    // custom_paint_color intentionally omitted — custom paint is a single
    // flat $85 line item added via the paint modal, never a per-shelf addon.
  };

  function loadAddonsConfig() {
    const c = window.ADDONS_CONFIG;
    if (!c || !Array.isArray(c.addons)) return [];
    return c.addons;
  }

  function getAddonDef(code) {
    return loadAddonsConfig().find(a => a.code === code) || null;
  }

  function shortKeyForCode(code) {
    return SELECTION_SHORT_KEYS[code] || null;
  }

  // Compute whether an addon's eligibility passes given the current shelf
  // state. Mirrors the backend rules in addon.service.js#validateSelection
  // so the UI hides addons the server would reject. Returns true when the
  // addon should be visible.
  function isAddonEligible(addonDef, refs, cfg) {
    if (!addonDef) return false;
    const e = addonDef.eligibility || {};

    const hwOpt = currentHardwareOption(refs, cfg);
    const hardwareYes = !!(hwOpt && hwOpt.hardware === 'yes');
    if (e.requiresHardware === true && !hardwareYes) return false;

    if (e.minThickness != null) {
      const t = currentThicknessNumeric(refs);
      if (!Number.isFinite(t) || t < e.minThickness) return false;
    }

    if (Array.isArray(e.materialFilter) && e.materialFilter.length) {
      const matName = currentMaterialName(refs);
      const slug = slugifyMaterialName(matName);
      if (!slug || !e.materialFilter.includes(slug)) return false;
    }
    return true;
  }

  function slugifyMaterialName(s) {
    return String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  // Walk every addon in the catalog, show/hide its row + refresh the price
  // label, and reset any selection that lost eligibility (so we don't ship
  // stale state to the backend). Called on init and after every
  // material/hardware/thickness change.
  function refreshAddons(refs, cfg, state) {
    const catalog = loadAddonsConfig();
    if (!refs.addons) return;

    for (const def of catalog) {
      const shortKey = shortKeyForCode(def.code);
      if (!shortKey) continue;
      const addonRefs = refs.addons[shortKey];
      if (!addonRefs) continue;

      const eligible = isAddonEligible(def, refs, cfg);
      addonRefs.row.hidden = !eligible;

      if (!eligible) {
        // Drop any in-flight selection so the backend never sees stale
        // input. Reset UI to defaults too.
        if (def.flatFee) {
          state.addons[shortKey] = { colorName: '' };
          if (addonRefs.text) addonRefs.text.value = '';
        } else {
          state.addons[shortKey] = (shortKey === 'puck')
            ? { qty: 0, items: [], notes: '', sketchFileName: '' }
            : { qty: 0, items: [] };
          if (addonRefs.qty)    addonRefs.qty.value = '0';
          if (addonRefs.units)  addonRefs.units.innerHTML = '';
          if (addonRefs.notes)  addonRefs.notes.value = '';
          if (addonRefs.sketch) { try { addonRefs.sketch.value = ''; } catch (e) { /* IE */ } }
          if (addonRefs.notesRow) addonRefs.notesRow.hidden = true;
        }
        continue;
      }

      // Eligible — refresh the inline price note so a Billy CSV-edit
      // post-page-load reflects after the next reload. Label format
      // matches the Liquid default ("channels · $35 each", "$85 one-time
      // per color") so the customer sees a consistent string.
      if (addonRefs.priceLabel) {
        addonRefs.priceLabel.textContent = formatAddonPriceLabel(def);
      }
    }
  }

  function formatAddonPriceLabel(def) {
    if (def.flatFee) return `($${Number(def.price).toFixed(0)} flat)`;
    return `($${Number(def.price).toFixed(0)} each)`;
  }

  // Build the per-unit input rows for an addon when its qty > 0. Renders
  // one block per channel/hole, with selects/text inputs derived from the
  // addon's perUnitInputs schema. Existing values are preserved across
  // qty changes by re-reading state.addons before redraw.
  function renderAddonUnits(shortKey, def, state, addonRefs) {
    if (!addonRefs.units) return;
    const qty = Math.max(0, parseInt(addonRefs.qty.value, 10) || 0);
    const inputs = Array.isArray(def.perUnitInputs) ? def.perUnitInputs : [];
    const items = state.addons[shortKey].items || [];

    const rows = [];
    for (let i = 0; i < qty; i++) {
      const item = items[i] || {};
      const fields = inputs.map(input => {
        const id = `cfg-addon-${shortKey}-${i + 1}-${input.key}`;
        const val = (item[input.key] != null && item[input.key] !== '')
          ? item[input.key]
          : defaultForInput(input);
        if (input.type === 'select' && Array.isArray(input.options)) {
          const opts = input.options.map(opt => (
            `<option value="${esc(opt)}"${opt === val ? ' selected' : ''}>${esc(opt)}</option>`
          )).join('');
          return `<select id="${id}" class="cfg-addon__unit-input" data-cfg-addon-unit-input="${esc(shortKey)}" data-unit-idx="${i}" data-unit-key="${esc(input.key)}" aria-label="${esc(input.label)} for ${esc(def.unit)} ${i + 1}">${opts}</select>`;
        }
        return `<input id="${id}" type="text" class="cfg-addon__unit-input" data-cfg-addon-unit-input="${esc(shortKey)}" data-unit-idx="${i}" data-unit-key="${esc(input.key)}" value="${esc(val)}" placeholder="${esc(input.default || '')}" aria-label="${esc(input.label)} for ${esc(def.unit)} ${i + 1}">`;
      }).join('');

      rows.push(
        `<div class="cfg-addon__unit${inputs.length === 1 ? ' cfg-addon__unit-flat' : ''}">
           <span class="cfg-addon__unit-label">${esc(def.unit[0].toUpperCase() + def.unit.slice(1))} ${i + 1}</span>
           ${fields}
         </div>`
      );
    }
    addonRefs.units.innerHTML = rows.join('');
  }

  // Sync the state.addons[shortKey].items array length with current qty,
  // preserving prior entries when the qty grows or shrinks. For each input
  // schema, fills the per-unit value with a sane default:
  //   - select inputs: input.default (when given) OR options[0] (so the
  //     state matches the first-option default the <select> renders)
  //   - text inputs:   input.default (typically the placeholder, e.g. 2-1/4")
  // Without this fallback, new items were created with empty strings and
  // the backend validation silently dropped them (required fields missing).
  function resizeAddonItems(shortKey, qty, def, state) {
    const block = state.addons[shortKey];
    if (!block || !Array.isArray(block.items)) return;
    const inputs = Array.isArray(def.perUnitInputs) ? def.perUnitInputs : [];
    while (block.items.length < qty) {
      const next = {};
      for (const input of inputs) next[input.key] = defaultForInput(input);
      block.items.push(next);
    }
    if (block.items.length > qty) block.items.length = qty;
  }

  function defaultForInput(input) {
    if (input.default != null && input.default !== '') return input.default;
    if (input.type === 'select' && Array.isArray(input.options) && input.options.length) {
      return input.options[0];
    }
    return '';
  }

  function wireAddons(refs, cfg, state) {
    if (!refs.addons) return;
    const catalog = loadAddonsConfig();
    for (const def of catalog) {
      const shortKey = shortKeyForCode(def.code);
      if (!shortKey) continue;
      const addonRefs = refs.addons[shortKey];
      if (!addonRefs) continue;

      if (def.flatFee) {
        // Custom Paint shape — single text input.
        if (addonRefs.text) {
          addonRefs.text.addEventListener('input', () => {
            state.addons[shortKey].colorName = addonRefs.text.value.trim();
            schedulePrice(refs, cfg, state);
          });
        }
        continue;
      }

      // Per-unit shape — qty input + dynamically-rendered unit rows.
      if (addonRefs.qty) {
        addonRefs.qty.addEventListener('input', () => {
          const qty = Math.max(0, parseInt(addonRefs.qty.value, 10) || 0);
          addonRefs.qty.value = String(qty);          // normalize (negative/NaN → 0)
          state.addons[shortKey].qty = qty;
          resizeAddonItems(shortKey, qty, def, state);
          renderAddonUnits(shortKey, def, state, addonRefs);
          // Puck-only: the notes/sketch row is only relevant when qty>0.
          if (shortKey === 'puck' && addonRefs.notesRow) {
            addonRefs.notesRow.hidden = qty === 0;
          }
          schedulePrice(refs, cfg, state);
        });
      }

      // Delegated handler for per-unit inputs (rendered dynamically).
      if (addonRefs.units) {
        addonRefs.units.addEventListener('change', (e) => {
          const el = e.target.closest('[data-cfg-addon-unit-input]');
          if (!el) return;
          const idx = parseInt(el.dataset.unitIdx, 10);
          const key = el.dataset.unitKey;
          if (!Number.isFinite(idx) || !key) return;
          const item = state.addons[shortKey].items[idx];
          if (!item) return;
          item[key] = String(el.value || '').trim();
          schedulePrice(refs, cfg, state);
        });
      }

      // Puck-specific extras. NB: notes + sketch DO affect price, because
      // the backend treats Puck as a sketch-required addon — without
      // either, validateSelection drops the block and addons_total stays
      // at 0. Both handlers re-schedule a price refresh so the customer
      // sees the $20-per-hole contribution flip in the moment they
      // supply the workshop with enough info to fabricate.
      if (shortKey === 'puck') {
        if (addonRefs.notes) {
          addonRefs.notes.addEventListener('input', () => {
            state.addons.puck.notes = addonRefs.notes.value.trim();
            schedulePrice(refs, cfg, state);
          });
        }
        if (addonRefs.sketch) {
          addonRefs.sketch.addEventListener('change', () => {
            const f = addonRefs.sketch.files && addonRefs.sketch.files[0];
            state.addons.puck.sketchFileName = f ? f.name : '';
            // Uploading a sketch triggers the Turnstile check (item 6).
            if (f) showPuckTurnstile(refs); else hidePuckTurnstile(refs);
            schedulePrice(refs, cfg, state);
          });
        }
      }
    }
  }

  // Build the addons payload for /api/price + /api/configure. Mirrors
  // the canonical shape canonicalizeSelection() expects on the backend.
  // Returns null when no addon is selected — keeps the price URL short.
  function buildAddonsApiBody(state) {
    const out = {};
    for (const [shortKey, code] of Object.entries({ led: 'led_channel_prep', puck: 'puck_light_hole' })) {
      const def = getAddonDef(code);
      if (!def) continue;
      const block = state.addons[shortKey];
      if (!block) continue;

      if (def.flatFee) {
        if (block.colorName) out[shortKey] = { colorName: block.colorName };
        continue;
      }
      if ((block.qty || 0) < 1) continue;
      const payload = {
        qty: block.qty,
        items: (block.items || []).slice(0, block.qty).map(it => ({ ...it })),
      };
      if (shortKey === 'puck') {
        if (block.notes) payload.notes = block.notes;
        if (block.sketchFileName) payload.sketchUploaded = true;
      }
      out[shortKey] = payload;
    }
    return Object.keys(out).length ? out : null;
  }

  function schedulePrice(refs, cfg, state, immediate) {
    clearTimeout(state.priceTimer);
    state.priceTimer = setTimeout(() => fetchPrice(refs, cfg, state),
      immediate ? 0 : PRICE_DEBOUNCE_MS);
  }

  // Resilient fetch: abort a hung request after `timeoutMs` and retry a couple
  // of times on a network-level failure, so a brief backend blip recovers
  // silently instead of surfacing "Network error" to the shopper. A real HTTP
  // response (404/500) is returned as-is — only thrown fetches are retried.
  async function fetchWithRetry(url, opts, attempts, timeoutMs) {
    attempts  = attempts  || 3;
    timeoutMs = timeoutMs || 8000;
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      const ctrl  = new AbortController();
      const timer = setTimeout(function () { ctrl.abort(); }, timeoutMs);
      try {
        const res = await fetch(url, Object.assign({ signal: ctrl.signal }, opts || {}));
        clearTimeout(timer);
        return res;
      } catch (e) {
        clearTimeout(timer);
        lastErr = e;
        if (i < attempts - 1) {
          await new Promise(function (r) { setTimeout(r, 400 * (i + 1)); });
        }
      }
    }
    throw lastErr;
  }

  // Record a configurator network failure so we can measure how often it
  // happens (Google Analytics is already loaded site-wide). Never throws.
  function reportCfgError(kind, err, key) {
    try {
      const msg = (err && (err.name === 'AbortError' ? 'timeout' : err.message)) || 'unknown';
      if (window.gtag) {
        window.gtag('event', 'configurator_' + kind + '_error', { error_message: msg, config_key: key || '' });
      }
      console.error('[configurator] ' + kind + ' network failure:', msg);
    } catch (e) { /* noop */ }
  }

  async function fetchPrice(refs, cfg, state) {
    // Mirror the current control state into the URL on every price refresh so
    // reloads / back-button / shared links restore the exact configuration.
    writeUrlState(refs, state.ctx);

    const sel = readSelections(refs, cfg);
    if (!sel) return setPriceDisplay(refs, null, '—');

    const apiMaterial = `material type ${sel.tier}`;
    const params = new URLSearchParams({
      product_id:     String(cfg.productId),
      material:       apiMaterial,
      material_name:  refs.material.value,
      finish:         sel.finishApi,
      type:           sel.productTypeApi,
      hardware:       sel.hardwareApi,
      thickness:      sel.thicknessPricing,
      depth_whole:    String(sel.depthWhole),
      depth_fraction: sel.depthFrac,
      width_whole:    String(sel.widthWhole),
      width_fraction: sel.widthFrac,
    });

    // Closet section: backend reuses slab no-hw rows (Billy confirmed
    // identical pricing) but routes by subtype so the cart line + variant
    // title read "Closet shelf / …" instead of "Slab shelf, no hardware".
    if (state.ctx && state.ctx.closetMode) params.set('subtype', 'closet');

    // Add-on contribution. When the customer has any addon selected,
    // include the canonical selection as a URL-encoded JSON param. The
    // backend re-validates eligibility and silently drops invalid blocks
    // on /api/price (defense-in-depth), so a stale selection never breaks
    // the price quote — it just shows the shelf-only price.
    const addonsBody = buildAddonsApiBody(state);
    if (addonsBody) params.set('addons', JSON.stringify(addonsBody));

    const key = params.toString();
    if (key === state.lastPriceKey) return;
    state.lastPriceKey = key;

    try {
      const res = await fetchWithRetry(`${cfg.apiUrl}/api/price?${key}`, { credentials: 'omit' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        state.lastPrice = null;
        return setPriceDisplay(refs, null, '—', explainPriceError(data.error, sel, cfg));
      }
      // Bundle model: data.price covers shelf + BAKED addons; cart-line
      // addons (paint, LED, puck under the bundle model) are summed
      // separately so the customer's live total reflects everything
      // they're about to add to cart. Falls back gracefully when the
      // backend predates this field (cart_line_addons_total → 0).
      const cartLineAddonsTotal = Number(data.cart_line_addons_total || 0);
      const grandTotal = Number(((data.price || 0) + cartLineAddonsTotal).toFixed(2));

      state.lastPrice              = grandTotal;
      state.lastShelfPrice         = data.shelf_price != null ? data.shelf_price : data.price;
      state.lastAddonsTotal        = data.addons_total || 0;
      state.lastCartLineAddonsTotal = cartLineAddonsTotal;
      state.lastPricedWidth        = data.priced_width;
      state.lastDepthRange         = data.depth_range;
      setPriceDisplay(refs, grandTotal, fmtMoney(grandTotal), '');
    } catch (err) {
      // A newer selection already superseded this request — don't overwrite
      // its result with a stale error.
      if (state.lastPriceKey !== key) return;
      reportCfgError('price', err, key);
      state.lastPrice = null;
      setPriceDisplay(refs, null, '—', 'Network error — please retry.');
    }
  }

  // Map backend errors + the current selection into a sentence that tells the
  // user (a) why pricing failed and (b) which control to change. Defaults
  // back to a generic "adjust dimensions" hint when nothing more specific applies.
  function explainPriceError(errorCode, sel, cfg) {
    if (errorCode === 'hardware_not_available') {
      return 'Maximum depth for Shelf with Hardware is 16″ — reduce depth to enable hardware.';
    }
    if (errorCode === 'invalid_depth') {
      return 'Depth is outside the available range. Pick a depth between 3″ and 28″.';
    }

    if (errorCode === 'rubio_not_allowed') {
      return 'Rubio Monocoat Oil Finish Pure is not available for this material.';
    }

    // Finish-tier mismatch — explicitly identify finish as the problem.
    if (sel && cfg && cfg.tierRules) {
      const rule = cfg.tierRules[String(sel.tier)] || { finished: true, unfinished: true };
      if (sel.finishApi === 'unfinished' && !rule.unfinished) {
        return 'Raw/Unfinished is not available for this material — please select a Conversion Varnish finish.';
      }
      if ((sel.finishApi === 'finished' || sel.finishApi === 'rubio') && !rule.finished) {
        return 'Conversion Varnish finishes are not available for this material — please select Raw/Unfinished.';
      }
    }

    // not_found — combination doesn't exist in the price table. Use the
    // selection to surface the most likely cause.
    if (errorCode === 'not_found' && sel) {
      // Shelf with Hardware only has prices for 1.75"+ stock in Billy's data.
      if (sel.hardwareApi === 'yes') {
        const t = parseFloat(sel.thicknessPricing);
        if (Number.isFinite(t) && t < 1.75) {
          return 'Shelf with Hardware is available only for thickness 1.75″ and greater. Increase thickness or switch to Slab.';
        }
      }
    }
    return 'Price unavailable for this combination. Try adjusting thickness, depth, or width.';
  }

  function setPriceDisplay(refs, price, text, status) {
    refs.price.textContent = text;
    if (refs.status) {
      refs.status.textContent = status || '';
      refs.status.hidden = !status;
    }
  }

  // ── selections ─────────────────────────────────────────────────────────
  function readSelections(refs, cfg) {
    const tier = currentTier(refs);
    if (!tier) return null;

    const hwOpt = currentHardwareOption(refs, cfg);
    if (!hwOpt) return null;

    const finishOpt = currentFinishOption(refs, cfg);
    if (!finishOpt) return null;

    const thicknessLabel = refs.thickness.value;
    if (!thicknessLabel) return null;

    const tOpt = refs.thickness.options[refs.thickness.selectedIndex];
    const isRange = tOpt && tOpt.dataset.kind === 'range';
    const thicknessOrderRecord = isRange
      ? formatDimension(parseInt(refs.thickExactW.value, 10), refs.thickExactF.value)
      : thicknessLabel;
    // pricingValue ≠ label when an entry like 1.25" reuses another row's
    // price ("1.5"). When the option carries a data-pricing attr, send
    // THAT to /api/price + /api/configure so the DB lookup hits the
    // correct row. Falls back to the label for legacy options that don't
    // declare a pricing value.
    const thicknessForPricing = (tOpt && tOpt.dataset.pricing) || thicknessLabel;

    const depthW = parseInt(refs.depthW.value, 10);
    const depthF = refs.depthF.value;
    const widthW = parseInt(refs.widthW.value, 10);
    const widthF = refs.widthF.value;
    if (!Number.isFinite(depthW) || !Number.isFinite(widthW)) return null;

    return {
      tier,
      hardwareApi:       hwOpt.hardware,
      hardwareLabel:     hwOpt.label,
      productTypeApi:    hwOpt.productType,
      finishApi:         finishOpt.finish,
      finishLabel:       finishOpt.label,
      thicknessLabel,
      thicknessPricing:  thicknessForPricing,
      thicknessOrderRecord,
      depthWhole:        depthW,
      depthFrac:         depthF,
      widthWhole:        widthW,
      widthFrac:         widthF,
    };
  }

  function currentMaterialName(refs) {
    const opt = refs.material.options[refs.material.selectedIndex];
    return opt ? opt.value : null;
  }

  function currentTier(refs) {
    const opt = refs.material.options[refs.material.selectedIndex];
    if (!opt) return null;
    return parseInt(opt.dataset.tier, 10);
  }

  function currentHardwareOption(refs, cfg) {
    const input = refs.hardwareList.querySelector('[data-cfg-hw-input]:checked');
    if (!input) return null;
    return cfg.hardwareOptions.find(o => o.id === input.value) || null;
  }

  function currentFinishOption(refs, cfg) {
    const input = refs.finishList.querySelector('[data-cfg-finish-input]:checked');
    if (!input) return null;
    const overrides = cfg.materialFinishOverrides || {};
    for (const k in overrides) {
      if (overrides[k].id === input.value) return overrides[k];
    }
    return cfg.finishOptions.find(o => o.id === input.value) || null;
  }

  function currentThicknessNumeric(refs) {
    const opt = refs.thickness.options[refs.thickness.selectedIndex];
    if (!opt) return null;
    const n = parseFloat(opt.dataset.numeric);
    return Number.isFinite(n) ? n : null;
  }

  function currentBuildStyle(refs, cfg) {
    if (!refs.buildStyles || !Array.isArray(cfg.buildStyles)) return null;
    const input = refs.buildStyles.querySelector('input[name="cfg-build-style"]:checked');
    if (!input) return null;
    return cfg.buildStyles.find(s => s.id === input.value) || null;
  }

  function resolveDepthRange(whole, frac, cfg) {
    let d = parseInt(whole, 10);
    if (frac && frac !== '0') d += 1;
    return cfg.depthRanges.find(r => d >= r.min && d <= r.max) || null;
  }

  // ── ATC ─────────────────────────────────────────────────────────────────
  // Flow: /api/configure → 5-second Shopify sync gap (countdown) →
  // /cart/add.js (with 422-retry backoff) → dispatch variant:add so Focal's
  // <cart-drawer> or <cart-notification-drawer> opens. The button is never
  // disabled by price state — only briefly disabled during in-flight steps
  // to prevent double-submit.
  function wireSubmit(form, refs, cfg, state) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      if (state.lastPrice == null) {
        setAtcStatus(refs, 'Calculating price — please wait.');
        return;
      }

      const sel = readSelections(refs, cfg);
      if (!sel) {
        setAtcStatus(refs, 'Please complete your configuration.');
        return;
      }

      // Puck light holes need a location description. Validate client-side so
      // the customer gets a plain-English prompt at the ATC instead of the
      // backend's raw "addon_not_eligible" (Billy 2026-07-01 items 4+5).
      if (state.addons.puck && state.addons.puck.qty > 0 && !state.addons.puck.notes) {
        setAtcStatus(refs, 'Please describe the puck light hole location(s) before adding to cart.');
        return;
      }

      // A puck sketch upload must pass the Turnstile check (item 6).
      if (puckSketchHasFile(refs)) {
        const passed = await ensurePuckTurnstile(refs, cfg);
        if (!passed) return;
      }

      setAtcBusy(refs.atc, true);
      setAtcLabel(refs.atc, 'Adding…');
      setAtcStatus(refs, '');

      try {
        // Step 1 — create the variant in our backend.
        const cfgBody = {
          product_id:     cfg.productId,
          material:       `material type ${sel.tier}`,
          material_name:  refs.material.value,
          finish:         sel.finishApi,
          type:           sel.productTypeApi,
          hardware:       sel.hardwareApi,
          thickness:      sel.thicknessPricing,
          depth_whole:    sel.depthWhole,
          depth_fraction: sel.depthFrac,
          width_whole:    sel.widthWhole,
          width_fraction: sel.widthFrac,
        };
        // Closet: same routing trick as /api/price above — backend reuses
        // slab no-hw price lookup but encodes a closet variant title.
        if (state.ctx && state.ctx.closetMode) cfgBody.subtype = 'closet';

        // Add-ons. /api/configure validates *strictly* (hard 400 on
        // ineligibility, unlike /api/price's silent drop) so a stale
        // selection here surfaces to the customer with the backend's
        // detail message — better than committing money on a config the
        // server can't fulfil.
        const addonsBody = buildAddonsApiBody(state);
        if (addonsBody) cfgBody.addons = addonsBody;

        const cfgRes = await fetch(`${cfg.apiUrl}/api/configure`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cfgBody),
        });
        const data = await cfgRes.json().catch(() => ({}));
        if (!cfgRes.ok || !data.variant_id) {
          const err = new Error(data.error || 'configure_failed');
          err.userMessage = friendlyConfigureError(data.error);
          throw err;
        }

        refs.variantId.value = data.variant_id;
        appendLineItemProperties(form, sel, refs, cfg, state);
        // Server-emitted addon properties (per-channel position/surface,
        // per-hole diameter, paint color name) get stamped onto the form
        // as hidden inputs so they ride along with the cart line. The
        // Puck sketch file input already lives on the form with its own
        // properties[…] name — it's included by FormData(form) on submit
        // and not duplicated here.
        appendAddonProperties(form, data.addon_properties || []);

        // Bundle model: each cart_line addon (paint, LED, puck) ships
        // as its own cart line via /cart/add.js items array, linked to
        // the shelf via the same _bundle_id (Adab 2026-06-10). The
        // shelf line ATC remains a FormData submit so the Puck sketch
        // file upload survives (Shopify's JSON add doesn't accept
        // files). Extra items POST after the shelf succeeds.
        const extraItems = Array.isArray(data.extra_items) ? data.extra_items : [];

        // Step 2 — sync gap with visible countdown. Shopify's cart-add
        // backend can lag Admin variant creation by a few seconds.
        await runAtcCountdown(refs.atc, ATC_SYNC_GAP_SECONDS);

        // Step 3 — AJAX cart-add via Focal's event-driven flow. Reassurance
        // messages rotate while we wait for the fetch + propagation backoff.
        startAtcReassurance(refs.atc);
        try {
          await focalCartAdd(form, extraItems);
        } finally {
          stopAtcReassurance(refs.atc);
        }

        // Success — Focal opens the drawer/notification via the variant:add
        // event we dispatched inside focalCartAdd.
      } catch (err) {
        setAtcStatus(refs, err.userMessage || friendlyAtcError(err));
      } finally {
        stopAtcReassurance(refs.atc);
        stopAtcCountdown(refs.atc);
        setAtcBusy(refs.atc, false);
        setAtcLabel(refs.atc, 'Add to cart');
      }
    }, /*useCapture*/ true);
  }

  function setAtcBusy(btn, busy) {
    if (!btn) return;
    btn.disabled = !!busy;
    if (busy) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  function setAtcLabel(btn, text) {
    if (btn) btn.textContent = text;
  }

  // ATC validation + add-to-cart errors render in the element next to the
  // button (data-cfg="atc-status") so they're visible where the customer is
  // looking; falls back to the top status line if that element is absent.
  function setAtcStatus(refs, text) {
    const el = refs.atcStatus || refs.status;
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  }

  // Map backend /api/configure error codes to plain-English messages so we
  // never surface a raw code like "addon_not_eligible" to the customer
  // (Billy 2026-07-01 item 4). Unknown codes get a generic fallback.
  const CONFIGURE_ERROR_MESSAGES = {
    addon_not_eligible: 'Please describe the puck light hole location(s) before adding to cart.',
  };
  function friendlyConfigureError(code) {
    if (!code) return null;
    return CONFIGURE_ERROR_MESSAGES[code]
      || 'Sorry — we couldn’t add this configuration to the cart. Please review your selections and try again.';
  }

  // ── Cloudflare Turnstile (puck sketch upload gate, Billy 2026-07-01 item 6) ─
  // The widget is only shown/rendered once a customer attaches a sketch file,
  // and the ATC is gated on a passed check only when a file is present — so
  // shoppers who don't upload anything are never challenged.
  const TURNSTILE_SITEKEY = '0x4AAAAAADqYGq2vcQYeTucs';

  function puckSketchHasFile(refs) {
    const inp = refs.addons && refs.addons.puck && refs.addons.puck.sketch;
    return !!(inp && inp.files && inp.files.length);
  }

  function renderPuckTurnstile(refs) {
    if (refs.__tsWidgetId != null || !refs.turnstileWidget) return;
    if (window.turnstile) {
      refs.__tsWidgetId = window.turnstile.render(refs.turnstileWidget, { sitekey: TURNSTILE_SITEKEY });
    } else {
      setTimeout(() => renderPuckTurnstile(refs), 200); // api.js still loading
    }
  }

  function showPuckTurnstile(refs) {
    if (!refs.turnstileWrap) return;
    refs.turnstileWrap.hidden = false;
    renderPuckTurnstile(refs);
  }

  function hidePuckTurnstile(refs) {
    if (!refs.turnstileWrap) return;
    refs.turnstileWrap.hidden = true;
    if (refs.turnstileErr) { refs.turnstileErr.hidden = true; refs.turnstileErr.textContent = ''; }
    if (window.turnstile && refs.__tsWidgetId != null) {
      try { window.turnstile.reset(refs.__tsWidgetId); } catch (e) { /* noop */ }
    }
  }

  // Returns true if the Turnstile check passed (or isn't required). Shows a
  // message at the ATC and returns false otherwise.
  async function ensurePuckTurnstile(refs, cfg) {
    const token = (window.turnstile && refs.__tsWidgetId != null)
      ? window.turnstile.getResponse(refs.__tsWidgetId)
      : null;
    if (!token) {
      setAtcStatus(refs, 'Please complete the verification check below the sketch upload before adding to cart.');
      return false;
    }
    try {
      const res = await fetch(`${cfg.apiUrl}/api/validate-turnstile`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token }),
      });
      const data = await res.json().catch(() => ({}));
      if (!data.valid) {
        if (window.turnstile && refs.__tsWidgetId != null) window.turnstile.reset(refs.__tsWidgetId);
        setAtcStatus(refs, 'The verification check failed — please try it again.');
        return false;
      }
      return true;
    } catch (e) {
      setAtcStatus(refs, 'Could not complete the verification check. Please try again.');
      return false;
    }
  }

  // Countdown "Preparing… 5s" → … → "1s", then resolves. Stored timer is
  // cleared on completion or by stopAtcCountdown (used in the error path).
  function runAtcCountdown(btn, seconds) {
    return new Promise((resolve) => {
      stopAtcCountdown(btn);
      let n = seconds;
      btn.textContent = `Preparing… ${n}s`;
      btn.__cfgCountdownTimer = setInterval(() => {
        n -= 1;
        if (n <= 0) {
          stopAtcCountdown(btn);
          resolve();
          return;
        }
        btn.textContent = `Preparing… ${n}s`;
      }, 1000);
    });
  }

  function stopAtcCountdown(btn) {
    if (btn && btn.__cfgCountdownTimer) {
      clearInterval(btn.__cfgCountdownTimer);
      btn.__cfgCountdownTimer = null;
    }
  }

  // Reassurance message rotation — picks a fresh message every 1.5s, never
  // repeats the same one twice in a row.
  function startAtcReassurance(btn) {
    stopAtcReassurance(btn);
    let lastIdx = -1;
    const pick = () => {
      let i;
      do {
        i = Math.floor(Math.random() * ATC_REASSURE_MSGS.length);
      } while (i === lastIdx && ATC_REASSURE_MSGS.length > 1);
      lastIdx = i;
      btn.textContent = ATC_REASSURE_MSGS[i];
    };
    pick();
    btn.__cfgReassureTimer = setInterval(pick, ATC_REASSURE_INTERVAL_MS);
  }

  function stopAtcReassurance(btn) {
    if (btn && btn.__cfgReassureTimer) {
      clearInterval(btn.__cfgReassureTimer);
      btn.__cfgReassureTimer = null;
    }
  }

  // Focal-native cart-add. Dispatch cart:prepare-bundled-sections first so
  // <cart-drawer> can include its own section id in the bundled response,
  // then POST to /cart/add.js with backoff retries on 422 (the cart backend
  // sometimes lags Admin variant creation even past our 5s gap). Finally
  // dispatch variant:add + cart:change so Focal opens the drawer/notification
  // and updates header cart counts.
  async function focalCartAdd(form, extraItems) {
    const variantInput = form.querySelector('input[name="id"]');
    const variantId = variantInput ? variantInput.value : '';
    const sectionsToBundle = ['variant-added'];

    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle },
    }));

    // Bundle addons go in their own JSON POST. Shopify's items[] mode is
    // the cleanest fit (single transactional add, atomic across paint /
    // LED / puck) but doesn't accept multipart file uploads — that's why
    // the shelf line uses FormData below. Add order matters for cart
    // display: Shopify's cart.js orders newest-first, so we add extras
    // FIRST and the shelf LAST so the shelf lands at the top of the cart
    // drawer, with the addon lines underneath. A failure in this step
    // is non-fatal — the shelf line still gets added below, just without
    // its surcharge companions.
    if (Array.isArray(extraItems) && extraItems.length) {
      try {
        await fetch(`${shopifyRoot()}cart/add.js`, {
          method:      'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type':    'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: extraItems }),
        });
      } catch (err) {
        console.warn('[configurator] failed to add bundle addons:', err);
      }
    }

    const formData = new FormData(form);
    formData.set('sections', sectionsToBundle.join(','));
    formData.set('sections_url', `${shopifyRoot()}variants/${variantId}`);

    const addJson = await postCartAddWithBackoff(formData);

    const cartContent = await fetch(`${shopifyRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then(r => r.json());
    cartContent.sections = addJson.sections;

    form.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: {
        items: addJson.items || [addJson],
        cart: cartContent,
      },
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'variant:add',
        cart: cartContent,
      },
    }));
  }

  async function postCartAddWithBackoff(formData) {
    const attempts = ATC_CART_ADD_BACKOFFS_MS.length + 1;
    let lastErr = null;
    for (let i = 0; i < attempts; i++) {
      const res = await fetch(`${shopifyRoot()}cart/add.js`, {
        method:      'POST',
        credentials: 'same-origin',
        headers:     { 'X-Requested-With': 'XMLHttpRequest' },
        body:        formData,
      });
      if (res.ok) return res.json();

      const body = await res.json().catch(() => ({}));
      const err = new Error(body.description || body.message || `cart_add_${res.status}`);
      err.status = res.status;
      err.body = body;
      lastErr = err;

      if (res.status === 422 && i < attempts - 1) {
        await new Promise(r => setTimeout(r, ATC_CART_ADD_BACKOFFS_MS[i]));
        continue;
      }
      throw err;
    }
    throw lastErr;
  }

  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function friendlyAtcError(err) {
    if (err && err.status === 422) {
      return 'Cart is taking longer than usual to sync. Please click Add to cart again.';
    }
    const msg = (err && err.message) || '';
    if (msg === 'configure_failed') {
      return "Couldn't create variant for this combination. Please adjust your configuration.";
    }
    return 'Could not add to cart. Please try again.';
  }

  // Stamp the server-returned addon properties as hidden inputs on the
  // form. The list comes from /api/configure as { name, value } pairs —
  // see addon.service.js#emitLineItemProperties. Idempotent: clears
  // prior stamps before adding so a re-tried ATC doesn't accumulate.
  function appendAddonProperties(form, props) {
    form.querySelectorAll('input[data-cfg-addon-prop]').forEach(el => el.remove());
    if (!Array.isArray(props) || !props.length) return;
    for (const { name, value } of props) {
      if (!name) continue;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value == null ? '' : String(value);
      input.dataset.cfgAddonProp = '1';
      form.appendChild(input);
    }
  }

  function appendLineItemProperties(form, sel, refs, cfg, state) {
    form.querySelectorAll('input[data-cfg-prop]').forEach(el => el.remove());

    const build = currentBuildStyle(refs, cfg);
    const props = [
      ['Material',  refs.material.value],
      ['Finish',    sel.finishLabel],
      ['Hardware',  sel.hardwareLabel],
      ['Thickness', sel.thicknessOrderRecord],
      ['Depth',     formatDimension(sel.depthWhole, sel.depthFrac)],
      ['Width',     formatDimension(sel.widthWhole, sel.widthFrac)],
    ];
    if (build) props.push(['Build Style', build.label]);
    props.push(['_price', String(state.lastPrice)]);

    props.forEach(([k, v]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${k}]`;
      input.value = v;
      input.dataset.cfgProp = '1';
      form.appendChild(input);
    });
  }

  // ── helpers ─────────────────────────────────────────────────────────────
  function formatDimension(whole, frac) {
    if (!frac || frac === '0') return `${whole}"`;
    return `${whole} ${frac}"`;
  }

  function fmtMoney(n) {
    if (n == null || isNaN(n)) return '—';
    return `$${Number(n).toFixed(2)}`;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Conservative CSS attribute-selector escape — our override ids are kebab-
  // case so we only need to neutralise quotes and backslashes.
  function cssEsc(s) {
    return String(s == null ? '' : s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  // ── Custom Paint modal ──────────────────────────────────────────────────
  // Replaces the old per-shelf $85 paint field. Adds ONE flat $85 line item
  // (the custom-paint-color product's base variant, emitted as
  // window.CUSTOM_PAINT by product-configurator.liquid) regardless of how many
  // shelves are configured — this fixes the bug where 3 shelves with a custom
  // colour were charged $85 x3. Dedupe guarantees a single paint line: if the
  // variant is already in the cart we update its colour note instead of adding
  // a second $85 line. Triggered by any [data-cpc-open] button.
  let cpcModalEl = null;

  function initPaintModal() {
    if (window.__cpcModalInit) return;
    window.__cpcModalInit = true;
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-cpc-open]');
      if (!trigger) return;
      e.preventDefault();
      openPaintModal();
    });
  }

  function paintPrice() {
    return (window.CUSTOM_PAINT && window.CUSTOM_PAINT.priceFormatted) || '$85';
  }

  function buildPaintModal() {
    const cp = window.CUSTOM_PAINT || {};
    const price = esc(paintPrice());
    const el = document.createElement('div');
    el.className = 'cpc-modal';
    el.hidden = true;
    el.innerHTML =
      '<div class="cpc-modal__backdrop" data-cpc-close></div>' +
      '<div class="cpc-modal__box" role="dialog" aria-modal="true" aria-labelledby="cpc-modal-title">' +
        '<button type="button" class="cpc-modal__x" data-cpc-close aria-label="Close">&times;</button>' +
        '<h3 class="cpc-modal__title" id="cpc-modal-title">Custom Paint Color</h3>' +
        '<p class="cpc-modal__lede">One flat <strong>' + price + '</strong> covers custom colour matching for your whole order — added once, not per shelf.</p>' +
        '<label class="cpc-modal__label" for="cpc-color">Paint colour / code</label>' +
        '<input id="cpc-color" type="text" class="cpc-modal__input" placeholder="e.g. BM Hale Navy HC-154" autocomplete="off">' +
        '<p class="cpc-modal__error" data-cpc-error hidden></p>' +
        '<div class="cpc-modal__actions">' +
          '<button type="button" class="cpc-modal__add" data-cpc-add>Add to cart — ' + price + '</button>' +
          (cp.url ? '<a class="cpc-modal__details" href="' + esc(cp.url) + '">See full details</a>' : '') +
        '</div>' +
        '<p class="cpc-modal__success" data-cpc-success hidden>&#10003; Custom paint added to your cart. <a href="/cart">View cart</a></p>' +
      '</div>';
    document.body.appendChild(el);

    el.querySelectorAll('[data-cpc-close]').forEach(b => b.addEventListener('click', closePaintModal));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cpcModalEl && !cpcModalEl.hidden) closePaintModal();
    });
    el.querySelector('[data-cpc-add]').addEventListener('click', submitPaint);
    return el;
  }

  function openPaintModal() {
    const cp = window.CUSTOM_PAINT || {};
    // No paint product wired up — fall back to the standalone page rather
    // than silently failing.
    if (!cp.variantId) {
      window.location.href = cp.url || '/products/custom-paint-color';
      return;
    }
    if (!cpcModalEl) cpcModalEl = buildPaintModal();
    cpcModalEl.querySelector('[data-cpc-success]').hidden = true;
    cpcModalEl.querySelector('[data-cpc-error]').hidden = true;
    const addBtn = cpcModalEl.querySelector('[data-cpc-add]');
    addBtn.disabled = false;
    addBtn.textContent = 'Add to cart — ' + paintPrice();
    cpcModalEl.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    const input = cpcModalEl.querySelector('#cpc-color');
    setTimeout(() => input && input.focus(), 30);
  }

  function closePaintModal() {
    if (!cpcModalEl) return;
    cpcModalEl.hidden = true;
    document.documentElement.style.overflow = '';
  }

  async function submitPaint() {
    if (!cpcModalEl) return;
    const cp = window.CUSTOM_PAINT || {};
    const input = cpcModalEl.querySelector('#cpc-color');
    const errEl = cpcModalEl.querySelector('[data-cpc-error]');
    const okEl  = cpcModalEl.querySelector('[data-cpc-success]');
    const addBtn = cpcModalEl.querySelector('[data-cpc-add]');
    const color = (input.value || '').trim();

    errEl.hidden = true;
    if (!color) {
      errEl.textContent = 'Please enter the paint colour or code.';
      errEl.hidden = false;
      input.focus();
      return;
    }
    if (!cp.variantId) {
      errEl.textContent = 'Custom paint is unavailable right now — please use the full paint page.';
      errEl.hidden = false;
      return;
    }

    addBtn.disabled = true;
    addBtn.textContent = 'Adding…';
    try {
      await addOrUpdatePaintLine(cp.variantId, color);
      okEl.hidden = false;
      addBtn.textContent = 'Added ✓';
      setTimeout(closePaintModal, 1400);
    } catch (err) {
      console.warn('[custom-paint] add failed:', err);
      errEl.textContent = "Sorry — couldn't add custom paint. Please try again.";
      errEl.hidden = false;
      addBtn.disabled = false;
      addBtn.textContent = 'Add to cart — ' + paintPrice();
    }
  }

  // Single-$85-line guarantee: if the paint variant is already in the cart,
  // update its colour note (cart/change.js) instead of adding a 2nd line.
  // Mirrors the configurator's own cart-add event sequence so Focal/Impact
  // opens the cart drawer / notification.
  async function addOrUpdatePaintLine(variantId, color) {
    const vid = Number(variantId);
    const props = { 'Paint Color': color };

    const cart = await fetch(`${shopifyRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then(r => r.json());
    const existing = (cart.items || []).find(it => it.id === vid);

    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: ['variant-added'] },
    }));

    if (existing) {
      await fetch(`${shopifyRoot()}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: existing.key, quantity: 1, properties: props }),
      });
    } else {
      await fetch(`${shopifyRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1, properties: props }] }),
      });
    }

    const cartContent = await fetch(`${shopifyRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then(r => r.json());

    document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: { items: cartContent.items, cart: cartContent },
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', cart: cartContent },
    }));
  }
})();
